import { pickBy } from 'lodash';
import { parseToRgb } from 'polished';

import { ICollection, INewDesignVariables } from 'types/collection';

type IDesign = ICollection['design'];
export type IDesignVariables = IDesign['variables'];

// The channels of a palette token, so CSS can build a translucent shade of it: CSS cannot
// decompose `var(--primary-color)` into rgba() components, and color-mix() is outside the
// browserslist target. Same tokens, second notation — not a mapping onto legacy names.
//   --primary-color-rgb: 242, 244, 241  ->  rgba(var(--primary-color-rgb), 0.4)
const PALETTE_RGB: Array<[string, keyof INewDesignVariables]> = [
  ['primaryColorRgb', 'primaryColor'],
  ['secondaryColorRgb', 'secondaryColor']
];

const toChannels = (color: string): string | undefined => {
  try {
    const { red, green, blue } = parseToRgb(color);

    return `${red}, ${green}, ${blue}`;
  } catch {
    // The value comes straight from the API and polished throws on anything unparseable.
    return undefined;
  }
};

/**
 * newDesign collections theme from `design.newVariables - Six tokens
 * primaryColor / secondaryColor / uiBackgroundColor / uiTextColor / accentColor /
 * coverTextColor.
 *
 * Merged, not swapped: newVariables is colors-only, so `variables` still supplies the fonts and
 * inputBorderRadius. Nothing here maps onto legacy colour keys any more — the redesign owns its
 * stylesheet (modules/ScrAppBookNew/styles) and its components, and both read the palette.
 */
export const resolveDesignVariables = (
  design: IDesign | undefined,
  isNewDesign: boolean
): IDesignVariables | undefined => {
  if (!design) return undefined;
  if (!isNewDesign || !design.newVariables) return design.variables;

  // An empty/absent new color must not blank out its legacy counterpart.
  const overrides = pickBy(design.newVariables, Boolean) as IDesignVariables;

  PALETTE_RGB.forEach(([key, token]) => {
    const color = overrides[token];
    const channels = color && toChannels(color as string);

    if (channels) overrides[key] = channels;
  });

  const resolved = { ...design.variables, ...overrides };

  // Some default palettes (Matcha, Basic Light) ship accent === page background, which hides
  // the decorative elements; the cover text colour is the designed contrast for that page.
  const { primaryColor, accentColor, coverTextColor } = resolved;
  const pageChannels = primaryColor && toChannels(primaryColor as string);
  const accentChannels = accentColor && toChannels(accentColor as string);
  const isAccentInvisible = Boolean(pageChannels) && pageChannels === accentChannels;

  if (isAccentInvisible && coverTextColor) resolved.accentColor = coverTextColor;

  return resolved;
};
